import { put, call, takeLatest, takeEvery, select } from 'redux-saga/effects';
import _ from 'lodash';
import * as actions from './actions';
import { Endpoints } from 'services/api';
import errorHandler from 'common/errorHandler';

const getTenant = (state) => state.userLogin.tenant;
const getUser = (state) => state.userLogin.user;

export const buildViewsListQueryString = (filters = {}, pageKey = null) => {
    let queryParams = [];
    if (filters.status) queryParams.push(`status=${encodeURIComponent(filters.status)}`);
    if (filters.datasetId) queryParams.push(`datasetId=${encodeURIComponent(filters.datasetId)}`);
    if (filters.schemaId) queryParams.push(`schemaId=${encodeURIComponent(filters.schemaId)}`);
    if (filters.search) queryParams.push(`search=${encodeURIComponent(filters.search)}`);
    if (pageKey) queryParams.push(`pageKey=${encodeURIComponent(pageKey)}`);
    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};

export const buildViewDataQueryString = (pageKey = null, limit = null) => {
    let queryParams = [];
    if (pageKey) queryParams.push(`pageKey=${encodeURIComponent(pageKey)}`);
    if (limit) queryParams.push(`limit=${encodeURIComponent(limit)}`);
    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};

export const buildViewHistoryQueryString = (pageKey = null) => {
    return pageKey ? `?pageKey=${encodeURIComponent(pageKey)}` : '';
};

function* getViewsList(api, tenantId, filters, pageKey) {
    let queryString = buildViewsListQueryString(filters, pageKey);
    let result = yield call([api, api.get], `${Endpoints.datasets}/api/v1/views${queryString}`, { tenantid: tenantId });
    return result.data;
}

function* getViewById(api, tenantId, viewId) {
    let result = yield call([api, api.get], `${Endpoints.datasets}/api/v1/views/${viewId}`, { tenantid: tenantId });
    return result.data;
}

function* createView(api, tenantId, viewData) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/views`, viewData, { tenantid: tenantId });
    return result.data;
}

function* updateView(api, tenantId, viewId, updateData) {
    let result = yield call([api, api.put], `${Endpoints.datasets}/api/v1/views/${viewId}`, updateData, { tenantid: tenantId });
    return result.data;
}

function* deleteView(api, tenantId, viewId) {
    yield call([api, api.delete], `${Endpoints.datasets}/api/v1/views/${viewId}`, { tenantid: tenantId });
}

function* runView(api, tenantId, viewId) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/views/${viewId}/run`, {}, { tenantid: tenantId });
    return result.data;
}

function* cloneView(api, tenantId, viewId, viewName, description, columnConfig) {
    let result = yield call([api, api.post], `${Endpoints.datasets}/api/v1/views/${viewId}/clone`, { viewName, description, columnConfig }, { tenantid: tenantId });
    return result.data;
}

function* getViewData(api, tenantId, viewId, pageKey, limit) {
    let queryString = buildViewDataQueryString(pageKey, limit);
    let result = yield call([api, api.get], `${Endpoints.datasets}/api/v1/views/${viewId}/data${queryString}`, { tenantid: tenantId });
    return result.data;
}

function* getViewHistory(api, tenantId, viewId, pageKey) {
    let queryParams = buildViewHistoryQueryString(pageKey);
    let result = yield call([api, api.get], `${Endpoints.datasets}/api/v1/views/${viewId}/history${queryParams}`, { tenantid: tenantId });
    return result.data;
}

// Watchers

function* watchGetViewsListRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getViewsList, api, tenant.tenantId, params.filters, params.pageKey);
        yield put(actions.getViewsListSuccess(result, params.append || false));
    } catch (error) {
        yield put(actions.getViewsListFailure(errorHandler(error)));
    }
}

function* watchGetRecentViewsRequest(api) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getViewsList, api, tenant.tenantId, {}, null);
        yield put(actions.getRecentViewsSuccess(result));
    } catch (error) {
        yield put(actions.getRecentViewsFailure(errorHandler(error)));
    }
}

function* watchGetViewByIdRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let view = yield call(getViewById, api, tenant.tenantId, params.viewId);
        yield put(actions.getViewByIdSuccess(view));
    } catch (error) {
        yield put(actions.getViewByIdFailure(errorHandler(error)));
    }
}

function* watchCreateViewRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let view = yield call(createView, api, tenant.tenantId, params.viewData);
        yield put(actions.createViewSuccess(view));
    } catch (error) {
        yield put(actions.createViewFailure(errorHandler(error)));
    }
}

function* watchUpdateViewRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let view = yield call(updateView, api, tenant.tenantId, params.viewId, params.updateData);
        yield put(actions.updateViewSuccess(view));
    } catch (error) {
        yield put(actions.updateViewFailure(errorHandler(error)));
    }
}

function* watchDeleteViewRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        yield call(deleteView, api, tenant.tenantId, params.viewId);
        yield put(actions.deleteViewSuccess(params.viewId));
    } catch (error) {
        yield put(actions.deleteViewFailure(errorHandler(error)));
    }
}

function* watchCloneViewRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let view = yield call(cloneView, api, tenant.tenantId, params.viewId, params.viewName, params.description, params.columnConfig);
        yield put(actions.cloneViewSuccess(view));
    } catch (error) {
        yield put(actions.cloneViewFailure(errorHandler(error)));
    }
}

function* watchRunViewRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(runView, api, tenant.tenantId, params.viewId);
        yield put(actions.runViewSuccess(result));
    } catch (error) {
        yield put(actions.runViewFailure(errorHandler(error)));
    }
}

function* watchGetViewDataRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getViewData, api, tenant.tenantId, params.viewId, params.pageKey, params.limit);
        yield put(actions.getViewDataSuccess(result, params.append || false));
    } catch (error) {
        yield put(actions.getViewDataFailure(errorHandler(error)));
    }
}

function* watchGetViewHistoryRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call(getViewHistory, api, tenant.tenantId, params.viewId, params.pageKey);
        yield put(actions.getViewHistorySuccess(result));
    } catch (error) {
        yield put(actions.getViewHistoryFailure(errorHandler(error)));
    }
}

function* watchGetUserViewResourcePermissionRequest(api) {
    try {
        let permission = { canAdd: false, canView: false, canEdit: false, canDelete: false };
        let tenant = yield select(getTenant);
        let user = yield select(getUser);
        let result = yield call([api, api.get], `${Endpoints.permissions}/api/v1/users/${user.userId}/permissions/resources/views`, { tenantid: tenant.tenantId });
        let permissionResponse = result.data;
        if (permissionResponse && permissionResponse.Items && permissionResponse.Items.length > 0) {
            let permissionActions = permissionResponse.Items.map(item => item.actions);
            permissionActions = _.union(...permissionActions);
            if (permissionActions.includes("add")) permission.canAdd = true;
            if (permissionActions.includes("view")) permission.canView = true;
            if (permissionActions.includes("edit")) permission.canEdit = true;
            if (permissionActions.includes("delete")) permission.canDelete = true;
        }
        yield put(actions.getUserViewResourcePermissionSuccess(permission));
    } catch (error) {
        yield put(actions.getUserViewResourcePermissionFailure(errorHandler(error)));
    }
}

function* watchGetUserViewPermissionRequest(api, { params }) {
    try {
        let tenant = yield select(getTenant);
        let result = yield call([api, api.get], `${Endpoints.permissions}/api/v1/permissions/views/${params.viewId}`, { tenantid: tenant.tenantId });
        yield put(actions.getUserViewPermissionSuccess(result.data));
    } catch (error) {
        yield put(actions.getUserViewPermissionFailure(errorHandler(error)));
    }
}

export default function* ({ api }) {
    yield takeLatest(actions.GET_VIEWS_LIST_REQUEST, watchGetViewsListRequest, api);
    yield takeLatest(actions.GET_RECENT_VIEWS_REQUEST, watchGetRecentViewsRequest, api);
    yield takeLatest(actions.GET_VIEW_BY_ID_REQUEST, watchGetViewByIdRequest, api);
    yield takeLatest(actions.CREATE_VIEW_REQUEST, watchCreateViewRequest, api);
    yield takeLatest(actions.UPDATE_VIEW_REQUEST, watchUpdateViewRequest, api);
    yield takeEvery(actions.DELETE_VIEW_REQUEST, watchDeleteViewRequest, api);
    yield takeLatest(actions.CLONE_VIEW_REQUEST, watchCloneViewRequest, api);
    yield takeLatest(actions.RUN_VIEW_REQUEST, watchRunViewRequest, api);
    yield takeLatest(actions.GET_VIEW_DATA_REQUEST, watchGetViewDataRequest, api);
    yield takeLatest(actions.GET_VIEW_HISTORY_REQUEST, watchGetViewHistoryRequest, api);
    yield takeEvery(actions.GET_USER_VIEW_PERMISSION_REQUEST, watchGetUserViewPermissionRequest, api);
    yield takeLatest(actions.GET_USER_VIEW_RESOURCE_PERMISSION_REQUEST, watchGetUserViewResourcePermissionRequest, api);
}
